import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import {
  QuestionSetDTO,
  QuestionSetItemClient,
  QuestionSetItemDTO,
  SearchRequestDTO_questionSetItemSearchRequestDTO,
} from '../../services/apiClient';
import useQuestionSetItemTypes from '../../hooks/useQuestionSetItemTypes';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';
import Table from '../common/Table';

interface QuestionSetFormProps {
  initialValues: QuestionSetDTO;
  onSubmit: (
    data: QuestionSetDTO,
    items: QuestionSetItemDTO[],
    itemsToDelete: string[],
  ) => void;
  onCancel: () => void;
}

const QuestionSetForm: React.FC<QuestionSetFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<QuestionSetDTO>(initialValues);
  const [items, setItems] = useState<QuestionSetItemDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { questionSetItemTypes } = useQuestionSetItemTypes();
  const [triggerNewRowEdit, setTriggerNewRowEdit] = useState(false);
  const [deletedItemIds, setDeletedItemIds] = useState<string[]>([]);

  const itemClient = new QuestionSetItemClient(API_BASE_URL, axiosInstance);
  const fetchItems = async (page: number, size: number, filters = {}) => {
    const searchRequest: SearchRequestDTO_questionSetItemSearchRequestDTO = {
      filters: {
        ...filters,
        questionSetId: formData.id
      },
      page,
      pageSize: size,
      sortBy: 'OrderSequence',
      sortDescending: false,
    };

    try {
      setIsLoading(true);
      const response = await itemClient.searchQuestionSetItems(searchRequest);
      const totalRecords = response.data?.totalCount ?? 0;
      setItems((response.data?.results as QuestionSetDTO[]) || []);
      setTotalRecords(totalRecords);
      setTotalPages(Math.ceil(totalRecords / size));
    } catch (error) {
      console.error(`Error fetching Payments:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if(formData.id)
      fetchItems(currentPage, pageSize);
  }, [formData.id, currentPage, pageSize]);

  const handleAddNewItem = () => {
    // Create a new item with the defaultHourlyRate as the price
    const newItem: QuestionSetItemDTO = {
      id: undefined,
      orderSequence: items.length + 1,
      question: '',
      data: '',
      questionSetItemTypeId: '',
      questionSetId: formData.id,
    };

    setItems([...items, newItem]);
    setTriggerNewRowEdit(true);
  };

  const handleEditItem = (
    updatedItem: QuestionSetItemDTO & { idx: number },
  ) => {
    const matchingType = questionSetItemTypes.find(
      (type) => type.id === updatedItem.questionSetItemTypeId
    );
  
    const updatedWithName: QuestionSetItemDTO = {
      ...updatedItem,
      questionSetItemTypeName: matchingType?.name ?? '',
    };
  
    setItems((prevItems) =>
      prevItems.map((item, i) => (i === updatedItem.idx ? updatedWithName : item))
    );
  };
  

  const handleDeleteItem = (item: QuestionSetItemDTO & { idx: number }) => {
    setItems((prev) => {
      const itemToRemove = prev[item.idx];

      // If the item has an ID, track it for deletion
      if (itemToRemove.id) {
        setDeletedItemIds((prevDeleted) =>
          prevDeleted.includes(itemToRemove.id!)
            ? prevDeleted
            : [...prevDeleted, itemToRemove.id!],
        );
      }

      // Return the updated list without the deleted item
      return prev.filter((_, i) => i !== item.idx);
    });
  };

  const handleChange = (key: keyof QuestionSetDTO) => (value: string) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData, items, deletedItemIds);
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="mb-4">
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange('name')(e.target.value)}
          placeholder="Enter Name"
        />
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
              Custom Questions
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              For dropdown questions, separate options in the{' '}
              <strong>Data</strong> field with commas. <br />
              <span className="italic">
                Example: <code>elephant,lion,bird</code>
              </span>
            </p>
            <Button variant="secondary" onClick={handleAddNewItem}>
              Add Item
            </Button>
          </div>
          <Table
            enableInlineEditing
            columns={[
              {
                label: 'Sequence',
                key: 'orderSequence',
                editable: true,
                editTemplate: (value, onChange) => (
                  <div style={{ width: '80px' }}>
                    <FormField
                      id="orderSequence"
                      type="number"
                      onChange={(e) => onChange(e.target.value)}
                      value={value}
                      label={''}
                    />
                  </div>
                ),
              },
              {
                label: 'Type',
                key: 'questionSetItemTypeId',
                render: (row: QuestionSetItemDTO) => <span>{row.questionSetItemTypeName}</span>,
                editable: true,
                editTemplate: (value, onChange) => (
                  <FormField
                    id="questionSetTypeId"
                    type="dropdown"
                    onChange={(e) => onChange(e.target.value)}
                    options={[
                      { value: '', label: 'Please select a type' },
                      ...questionSetItemTypes.map((m) => ({
                        value: m.id ?? '',
                        label: `${m.name}`,
                      })),
                    ]}
                    value={value}
                    label={''}
                  />
                ),
              },
              {
                label: 'Question',
                key: 'question',
                editable: true,
                editTemplate: (value, onChange) => (
                  <FormField
                    id="question"
                    type="text"
                    onChange={(e) => onChange(e.target.value)}
                    value={value}
                    label={''}
                  />
                ),
              },
              {
                label: 'Data',
                key: 'data',
                editable: true,
                editTemplate: (value, onChange) => (
                  <FormField
                    id="data"
                    type="text"
                    onChange={(e) => onChange(e.target.value)}
                    value={value}
                    label={''}
                  />
                ),
              },
            ]}
            data={items.map((item, idx) => ({ ...item, idx }))}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
            actions={{
              onEdit: handleEditItem,
              onDelete: handleDeleteItem,
            }}
            triggerNewRowEdit={triggerNewRowEdit}
            setTriggerNewRowEdit={setTriggerNewRowEdit}
          />
        </div>
      </div>
      <div className="flex justify-end space-x-4">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Save</Button>
      </div>
    </form>
  );
};

export default QuestionSetForm;
